import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, FormHelperText, Skeleton } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { usePrompt } from 'src/hooks/use-prompt';
import { Check } from 'src/icons/check';
import { Close } from 'src/icons/close';
import { Edit } from 'src/icons/edit';
import { z } from 'zod';
import { InputField } from '../atoms/input-field';
import { LoadingButton } from '../atoms/loading-button/loading-button';
import TruncatedText from '../atoms/truncated-text/truncated-text';
import { useEditableNameFormValidationSchema } from './editable-name/schema/use-form-validation-schema';

interface Props {
  name: string;
  onUpdateName: (name: string) => Promise<void>;
  isLoadingName: boolean;
}

export const EditableName = ({ name, onUpdateName, isLoadingName }: Props) => {
  const [isEditModeActive, setIsEditModeActive] = useState<boolean>(false);

  const formValidationSchema = useEditableNameFormValidationSchema();

  type FormValues = z.infer<typeof formValidationSchema>;

  const defaultValues: FormValues = {
    name: '',
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isSubmitting, isDirty, errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formValidationSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const handleCancel = () => {
    setIsEditModeActive(false);
    reset(defaultValues);
  };

  const handleEditName = async (data: FormValues) => {
    await onUpdateName(data.name);
    handleCancel();
  };

  useEffect(() => {
    if (name && isEditModeActive) {
      reset({ name });
    }
  }, [name, isEditModeActive]);

  usePrompt(isDirty);

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      {isEditModeActive ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              height: '50px',
            }}>
            <Controller
              control={control}
              name="name"
              render={({
                field: { value, name, onBlur, onChange },
                fieldState: { error },
              }) => (
                <InputField
                  fullWidth
                  sx={{ height: '100%', width: 250 }}
                  name={name}
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}
                  required
                  inputProps={{ sx: { fontSize: '24px', fontWeight: 600 } }}
                  error={!!error}
                  id="edit_name_input"
                />
              )}
            />
            <LoadingButton
              variant="outlinedContained"
              onClick={handleSubmit(handleEditName)}
              disabled={!isValid}
              loading={isSubmitting}
              sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
              size="large">
              <Check />
            </LoadingButton>
            <Button
              variant="text"
              onClick={handleCancel}
              size="large"
              sx={{
                height: '100%',
                color: 'text.primary',
                '&:hover': {
                  color: 'text.primary',
                },
                '&:active': {
                  color: 'text.primary',
                },
                '&:focus': {
                  color: 'text.primary',
                },
              }}>
              <Close />
            </Button>
          </Box>
          {errors?.name?.message ? (
            <FormHelperText error>{errors?.name?.message}</FormHelperText>
          ) : null}
        </Box>
      ) : (
        <Box
          onClick={() => setIsEditModeActive(true)}
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            cursor: 'pointer',
          }}>
          {isLoadingName ? (
            <Skeleton height={25} width={50} />
          ) : (
            <>
              <TruncatedText text={name} maxCharacters={35} variant="h4" />
              <Button
                variant="text"
                onClick={() => setIsEditModeActive(true)}
                size="large"
                sx={{
                  color: 'text.primary',
                  '&:hover': {
                    color: 'text.primary',
                  },
                  '&:active': {
                    color: 'text.primary',
                  },
                  '&:focus': {
                    color: 'text.primary',
                  },
                }}>
                <Edit />
              </Button>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
